import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Announcementslider from './Announcementslider.jsx';
import CustomNavbar from "./CustomPermissions/CustomNavbar";

import { getApiUrl, API_ENDPOINTS, API_BASE_URL } from "../config/api";
import { exportRecordsToExcel } from "../utils/exportUtils";
import { Button, Card, PageHeader } from "./common";

function Records() {
  const [data, setData] = useState(null);
  const [dailyEntryData, setDailyEntryData] = useState([]);
  const [pipelineandturnedup, setPipelineandturnedup] = useState([]);
  const [candidateRecords, setCandidateRecords] = useState({});
  const [pipelineRecords, setPipelineRecords] = useState({});
  const [activeFilter, setActiveFilter] = useState({});
  const [uniquePipelineCounts, setUniquePipelineCounts] = useState({});
  const [expandedRows, setExpandedRows] = useState(null);
  const [nestedExpandedRows, setNestedExpandedRows] = useState({});


  // Fetch data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const accessToken = localStorage.getItem("access_token");
        if (accessToken) {
          const response = await axios.get(getApiUrl(API_ENDPOINTS.GET_ALL_RECORDS), {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          });

          const { records, monthly_records, pipelineandturnedup } = response.data;

          setData(records || []);
          setDailyEntryData(monthly_records || []);
          setPipelineandturnedup(pipelineandturnedup || []);

          // Pre-fetch PIPELINE records for all users
          const allUsers = monthly_records.map((entry) => entry.user);
          const pipelineRecordsMap = {};
          const uniquePipelineCountsMap = {};

          for (const userId of allUsers) {
            const userPipelineRecords = await fetchCandidateRecords(userId, "pipeline");
            pipelineRecordsMap[userId] = userPipelineRecords;

            const userMonthlyRecord = monthly_records.find(entry => entry.user === userId);
            const uniqueCount = userMonthlyRecord?.pipeline_unique || 0;
            uniquePipelineCountsMap[userId] = uniqueCount;
          }

          setPipelineRecords(pipelineRecordsMap);
          setUniquePipelineCounts(uniquePipelineCountsMap);

        }
      } catch (error) {
        console.error("There was a problem with the fetch operation:", error);
      }
    };

    fetchData();
  }, []);

  // Function to fetch candidate records
  const fetchCandidateRecords = async (userId, filterType) => {
    try {
      const accessToken = localStorage.getItem("access_token");
      const response = await axios.get(
        getApiUrl(`/GetAllCandidateOfUser/${userId}/`),
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
          params: { status: filterType },
        }
      );
      return response.data.data;
    } catch (error) {
      console.error("Error fetching candidate records:", error);
      return [];
    }
  };

  // Toggle expanded row for a specific user and filter type
  const toggleExpandedRow = async (userId, filterType) => {
    if (expandedRows === userId && activeFilter[userId] === filterType) {
      setExpandedRows(null);
      setActiveFilter((prev) => ({ ...prev, [userId]: null }));
    } else {
      if (filterType === 'pipeline') {
        const records = pipelineRecords[userId] || [];
        setCandidateRecords((prev) => ({ ...prev, [userId]: records }));
      } else {
        const records = await fetchCandidateRecords(userId, filterType);
        setCandidateRecords((prev) => ({ ...prev, [userId]: records }));
      }

      setExpandedRows(userId);
      setActiveFilter((prev) => ({ ...prev, [userId]: filterType }));
    }
  };

  // Toggle nested expanded row
  const toggleNestedExpandedRow = (userId, recordId) => {
    setNestedExpandedRows((prev) => {
      const updatedNestedRows = { ...prev };
      if (updatedNestedRows[userId] === recordId) {
        delete updatedNestedRows[userId];
      } else {
        updatedNestedRows[userId] = recordId;
      }
      return updatedNestedRows;
    });
  };

  // Helper function for unique pipelines
  const getUniquePipelines = (records) => {
    const seenCombinations = new Set();
    return records.filter((record) => {
      const combinationKey = `${record.hospital || 'unknown'}_${record.city || 'unknown'}_${record.state || 'unknown'}_${record.speciality || 'unknown'}`.toLowerCase();
      if (seenCombinations.has(combinationKey)) {
        return false;
      }
      seenCombinations.add(combinationKey);
      return true;
    });
  };

  // Use useMemo to memoize sorted data for performance optimization
  const sortedData = useMemo(() => {
    if (!dailyEntryData || !pipelineandturnedup) return [];

    return dailyEntryData
      .map((entry) => {
        const pipeline = pipelineandturnedup.find((item) => item.user === entry.user)?.pipline || 0;
        const turnedUpRatio = entry.turned_up && entry.resume_forwarded
          ? ((entry.turned_up / entry.resume_forwarded) * 100)
          : 0;
        const correspondingRecord = data.find((record) => record.id === entry.user);
        const targetRev = correspondingRecord ? correspondingRecord.revenue : 0;
        return {
          ...entry,
          pipeline,
          turnedUpRatio,
          targetRev,
        };
      })
      .sort((a, b) => {
        if (b.selected !== a.selected) return b.selected - a.selected;
        if (b.targetRev === 0 && a.targetRev !== 0) return -1;
        if (a.targetRev === 0 && b.targetRev !== 0) return 1;
        if (b.turnedUpRatio !== a.turnedUpRatio) return b.turnedUpRatio - a.turnedUpRatio;
        if (b.resume_forwarded !== a.resume_forwarded) return b.resume_forwarded - a.resume_forwarded;
        return b.pipeline - a.pipeline;
      });
  }, [dailyEntryData, pipelineandturnedup, data]);

  // Calculate totals
  const totalForwarded = sortedData.reduce((acc, curr) => acc + curr.resume_forwarded, 0);
  const totalPipeline = sortedData.reduce((acc, curr) => acc + curr.pipeline, 0);
  const totalTurnedUp = sortedData.reduce((acc, curr) => acc + curr.turned_up, 0);
  const totalSelected = sortedData.reduce((acc, curr) => acc + curr.selected, 0);
  const totalBackout = sortedData.reduce((acc, curr) => acc + (curr.backout || 0), 0);
  const totalRevenue = sortedData.reduce((acc, curr) => acc + (isNaN(curr.revenue) ? 0 : parseFloat(curr.revenue)), 0);
  const totalTargetRevenue = sortedData.reduce((acc, curr) => {
    const correspondingRecord = data.find((record) => record.id === curr.user);
    return acc + (correspondingRecord?.revenue || 0);
  }, 0);
  const totalPercentage = (totalRevenue / totalTargetRevenue) * 100;

  return (
    <div style={{ display: "flex" }}>
      {/* Sidebar */}
      <CustomNavbar />

      {/* Main Content Area */}
      <div className="flex-1 min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
        <Announcementslider />

        {/* Page Content */}
        <div className="p-6">
          {/* Page Header */}
          <div className="bg-white rounded-xl shadow-lg p-4 mb-6 border-b-4 border-blue-500">
            <div className="flex justify-between items-center flex-wrap gap-3">
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                  👥 Team Leader Records
                </h1>
                <p className="text-gray-600 mt-1">Current Month Performance</p>
              </div>
              <Button
                variant="success"
                size="md"
                icon="📥"
                onClick={() => {
                  const exportData = sortedData.map((entry) => {
                    const user = data.find((r) => r.id === entry.user);
                    return {
                      name: user?.name || "",
                      username: user?.username || "",
                      resume_forwarded: entry.resume_forwarded || 0,
                      pipeline_unique: uniquePipelineCounts[entry.user] || 0,
                      pipeline: entry.pipeline || 0,
                      turned_up: entry.turned_up || 0,
                      selected: entry.selected || 0,
                      backout: entry.backout || 0,
                      revenue: entry.revenue || 0,
                      target: user?.revenue || 0,
                    };
                  });
                  exportRecordsToExcel(exportData, `Team_Leader_Records_${new Date().toISOString().split('T')[0]}`);
                }}
              >
                Export to Excel
              </Button>
            </div>
          </div>

          {/* Records Table Card */}
          <Card shadow="xl">
            <table className="min-w-full bg-white border border-gray-300 rounded-lg overflow-hidden">
              <thead>
                <tr className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
                  <th className="py-3 px-4 border-b font-semibold">👤 Image</th>
                  <th className="py-3 px-4 border-b font-semibold">📛 Name</th>
                  <th className="py-3 px-4 border-b font-semibold">📨 Forwarded</th>
                  <th className="py-3 px-4 border-b font-semibold">⏳ Pipeline</th>
                  <th className="py-3 px-4 border-b font-semibold">👥 Turned Up</th>
                  <th className="py-3 px-4 border-b font-semibold">✅ Selected</th>
                  <th className="py-3 px-4 border-b font-semibold">❌ Backout</th>
                  <th className="py-3 px-4 border-b font-semibold">💰 Revenue</th>
                  <th className="py-3 px-4 border-b font-semibold">📊 Achievement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-300">
                {sortedData.map((dailyEntry) => {
                  const correspondingRecord = data.find((record) => record.id === dailyEntry.user);
                  const imageSrc = correspondingRecord ? `${API_BASE_URL}${correspondingRecord.image}` : '';
                  const userName = correspondingRecord ? correspondingRecord.name : '';
                  const targetRev = correspondingRecord ? correspondingRecord.revenue : '';
                  const target = correspondingRecord ? correspondingRecord.target : '';
                  const pipeline = pipelineandturnedup.find((entry) => entry.user === dailyEntry.user)?.pipline || '';
                  const isExpanded = expandedRows === dailyEntry.user;

                  return (
                    <React.Fragment key={dailyEntry.user}>
                      {/* Main Row */}
                      <tr style={{ fontSize: "14px" }}>
                        <td className="py-2 px-4 border-b text-center">
                          {imageSrc && <img src={imageSrc} className="w-auto" style={{ height: "100px" }} alt="" />}
                        </td>
                        <td className="py-2 px-4 border-b text-center">{userName}</td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() => toggleExpandedRow(dailyEntry.user, "resume_forwarded")}
                        >
                          {dailyEntry.resume_forwarded}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() => toggleExpandedRow(dailyEntry.user, "pipeline")}
                        >
                          {uniquePipelineCounts[dailyEntry.user] || 0} / {pipeline === "" ? "0" : pipeline}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() => toggleExpandedRow(dailyEntry.user, "turned_up")}
                        >
                          {dailyEntry.turned_up}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() => toggleExpandedRow(dailyEntry.user, "selected")}
                        >
                          {dailyEntry.selected} / {target}
                        </td>
                        <td
                          className="py-2 px-4 border-b text-center cursor-pointer text-blue-500"
                          onClick={() => toggleExpandedRow(dailyEntry.user, "backout")}
                        >
                          {dailyEntry.backout || 0}
                        </td>
                        <td className="py-2 px-4 border-b text-center">
                          {isNaN(dailyEntry.revenue) ? "0.00" : parseFloat(dailyEntry.revenue).toFixed(2)} / {parseFloat(targetRev).toFixed(2)} lac
                        </td>
                        <td className={`py-2 px-4 border-b text-center ${(dailyEntry.revenue / targetRev) * 100 <= 50
                            ? "text-red-500"
                            : (dailyEntry.revenue / targetRev) * 100 < 91
                              ? "text-yellow-500"
                              : (dailyEntry.revenue / targetRev) * 100 < 115
                                ? "text-green-500"
                                : "text-blue-700"
                          }`}>
                          {dailyEntry.revenue !== 0 ? (
                            ((dailyEntry.revenue / targetRev) * 100).toFixed(2)
                          ) : (
                            <span className="text-red-500">0</span>
                          )}
                          %
                        </td>
                      </tr>

                      {/* Extended Row */}
                      {isExpanded && (
                        <tr>
                          <td colSpan="9" className="p-4 bg-gray-100">
                            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                              <thead className="bg-gray-300">
                                <tr>
                                  <th className="py-2 px-4 border-b">Candidate Name</th>
                                  <th className="py-2 px-4 border-b">Hospital</th>
                                  <th className="py-2 px-4 border-b">City</th>
                                  <th className="py-2 px-4 border-b">State</th>
                                  <th className="py-2 px-4 border-b">Speciality</th>
                                  <th className="py-2 px-4 border-b">Status</th>
                                  <th className="py-2 px-4 border-b">Revenue</th>
                                </tr>
                              </thead>
                              <tbody>
                                {candidateRecords[dailyEntry.user]?.map((record) => {
                                  const isNestedExpanded = nestedExpandedRows[dailyEntry.user] === record.id;
                                  return (
                                    <React.Fragment key={record.id}>
                                      <tr
                                        onClick={() => toggleNestedExpandedRow(dailyEntry.user, record.id)}
                                        style={{
                                          cursor: "pointer",
                                          backgroundColor: isNestedExpanded ? "#f0f8ff" : "transparent",
                                        }}
                                      >
                                        <td className="py-4 px-4 border-b text-center">{record.candidate_name}</td>
                                        <td className="py-4 px-4 border-b text-center">{record.hospital}</td>
                                        <td className="py-4 px-4 border-b text-center">{record.city}</td>
                                        <td className="py-4 px-4 border-b text-center">{record.state}</td>
                                        <td className="py-4 px-4 border-b text-center">{record.speciality}</td>
                                        <td className="py-4 px-4 border-b text-center" style={{
                                          color: record.status === "resume_forwarded" ? "green"
                                            : record.status === "pipeline" ? "green"
                                              : record.status === "turned_up" ? "orange"
                                                : record.status === "backout" ? "red"
                                                  : record.status === "not selected" ? "gray"
                                                    : record.status === "selected" ? "blue"
                                                      : "black"
                                        }}>
                                          {record.status}
                                        </td>
                                        <td className="py-4 px-4 border-b text-center">{record.revenue}</td>
                                      </tr>
                                      {isNestedExpanded && (
                                        <tr style={{ backgroundColor: "#96DED1", border: "3px solid #f0f8ff" }}>
                                          <td colSpan="7" className="p-3">
                                            <div><strong>Remark:</strong> {record.remark}</div>
                                            <div><strong>Date Entered:</strong> {record.date_entered}</div>
                                          </td>
                                        </tr>
                                      )}
                                    </React.Fragment>
                                  );
                                })}
                              </tbody>
                            </table>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}

                {/* Totals Row */}
                <tr className="bg-gray-300" style={{ fontSize: "17px" }}>
                  <td className="py-2 px-4 border-b text-center font-bold">Total</td>
                  <td className="py-2 px-4 border-b"></td>
                  <td className="py-2 px-4 border-b text-center font-bold">{totalForwarded}</td>
                  <td className="py-2 px-4 border-b text-center font-bold">{totalPipeline}</td>
                  <td className="py-2 px-4 border-b text-center font-bold">{totalTurnedUp}</td>
                  <td className="py-2 px-4 border-b text-center font-bold">{totalSelected}</td>
                  <td className="py-2 px-4 border-b text-center font-bold">{totalBackout}</td>
                  <td className="py-2 px-4 border-b text-center font-bold">
                    <span className={` ${totalPercentage <= 50
                        ? "text-red-500"
                        : totalPercentage < 91
                          ? "text-yellow-500"
                          : totalPercentage * 100 < 115
                            ? "text-green-500"
                            : "text-blue-700"
                      }`}>
                      {totalRevenue.toFixed(2)}{" "}
                    </span>
                    /{totalTargetRevenue.toFixed(2)} lac
                  </td>
                  <td className={`py-2 px-4 border-b text-center font-bold ${totalPercentage <= 50
                      ? "text-red-500"
                      : totalPercentage < 91
                        ? "text-yellow-500"
                        : totalPercentage * 100 < 115
                          ? "text-green-500"
                          : "text-blue-700"
                    }`}>
                    {totalPercentage.toFixed(2)}%
                  </td>
                </tr>
              </tbody>
            </table>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default Records;
